/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect } from 'react';
import { apiService } from '../../services/api';
import './PersonaChat.css';

const PersonaChat = ({ persona, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
//  const [isConnected, setIsConnected] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
 const [audioChunks, setAudioChunks] = useState([]);
  const [audioChunksRef, setAudioChunksRef] = useState([]);
  const [recordingStartTime, setRecordingStartTime] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);

  const messagesEndRef = useRef(null);
//  const audioRef = useRef(null);

  // Initialize media recorder
  useEffect(() => {
    const initMediaRecorder = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 44100
          } 
        });
        
        // Try different MIME types for better browser compatibility
        let mimeType = 'audio/webm;codecs=opus';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/webm';
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = 'audio/mp4';
            if (!MediaRecorder.isTypeSupported(mimeType)) {
              mimeType = 'audio/wav';
              if (!MediaRecorder.isTypeSupported(mimeType)) {
                mimeType = '';
              }
            }
          }
        }
        
        const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
        setMediaRecorder(recorder);

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            setAudioChunks(prev => [...prev, event.data]);
            setAudioChunksRef(prev => [...prev, event.data]);
          }
        };

        recorder.onstop = () => {
          const blob = new Blob(audioChunksRef, { type: mimeType || 'audio/webm' });
          if (blob.size > 0) {
            processVoiceMessage(blob);
          }
          setAudioChunks([]);
          setAudioChunksRef([]);
        };

        recorder.onerror = (event) => {
          console.error('MediaRecorder error:', event.error);
        };

        return () => {
          stream.getTracks().forEach(track => track.stop());
        };
      } catch (error) {
        console.error('Error accessing microphone:', error);
      }
    };

    initMediaRecorder();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recording duration timer
  useEffect(() => {
    let interval = null;
    if (isRecording && recordingStartTime) {
      interval = setInterval(() => {
        setRecordingDuration(Math.floor((Date.now() - recordingStartTime) / 1000));
      }, 1000);
    } else if (!isRecording) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isRecording, recordingStartTime]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const startRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'inactive') {
      setAudioChunks([]);
      setAudioChunksRef([]);
      setRecordingStartTime(Date.now());
      setRecordingDuration(0);
      setIsRecording(true);
      mediaRecorder.start(1000);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      setIsRecording(false);
      setRecordingStartTime(null);
      mediaRecorder.stop();
    }
  };

  const processVoiceMessage = async (audioBlob) => {
    setIsProcessing(true);
    try {
      // Send voice message to backend
      const result = await apiService.processAudio(
        audioBlob, 
        'chat_message', 
        'Voice message to persona'
      );
      
      // Add user message
      const userMessage = {
        id: Date.now(),
        type: 'user',
        content: result.transcript,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, userMessage]);
      
      // Send to persona chat
      await sendMessage(result.transcript);
      
    } catch (error) {
      console.error('Error processing voice message:', error);
      alert('Error processing voice message. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const sendMessage = async (messageText) => {
    if (!messageText.trim()) return;

    // Add user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');

    try {
      // Send message to persona chat endpoint
      const response = await fetch(`${apiService.baseUrl}/api/persona/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          persona_id: persona.persona_id,
          message: messageText,
          voice_id: persona.voice_id
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const result = await response.json();
      
      // Add persona response
      const personaMessage = {
        id: Date.now() + 1,
        type: 'persona',
        content: result.response,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, personaMessage]);

    } catch (error) {
      console.error('Error sending message:', error);
      alert('Error sending message. Please try again.');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputMessage);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="persona-chat">
      <div className="chat-header">
        <div className="persona-info">
          <div className="persona-avatar">
            {persona.name.charAt(0).toUpperCase()}
          </div>
          <div className="persona-details">
            <h2>{persona.name}</h2>
            <p className="persona-status">Online</p>
          </div>
        </div>
        <button onClick={onBack} className="btn-back">
          ← Back to Persona Creation
        </button>
      </div>

      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="welcome-message">
            <h3>👋 Hi! I'm {persona.name}</h3>
            <p>I'm here to chat with you using my own voice and personality!</p>
            <p>You can type a message or use the voice button to speak with me.</p>
          </div>
        ) : (
          messages.map((message) => (
            <div key={message.id} className={`message ${message.type}`}>
              <div className="message-content">
                <div className="message-text">{message.content}</div>
                <div className="message-time">{formatTime(message.timestamp)}</div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input">
        <form onSubmit={handleSubmit} className="input-form">
          <div className="input-container">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder={`Type a message to ${persona.name}...`}
              className="message-input"
              disabled={isProcessing}
            />
            
            <div className="input-actions">
              {!isRecording ? (
                <button
                  type="button"
                  onClick={startRecording}
                  className="btn-voice"
                  disabled={!mediaRecorder || isProcessing}
                  title="Hold to record voice message"
                >
                  🎤
                </button>
              ) : (
                <button
                  type="button"
                  onClick={stopRecording}
                  className="btn-stop-recording"
                  title="Stop recording"
                >
                  ⏹️
                </button>
              )}
              
              <button
                type="submit"
                className="btn-send"
                disabled={!inputMessage.trim() || isProcessing}
              >
                {isProcessing ? '⏳' : '➤'}
              </button>
            </div>
          </div>
          
          {isRecording && (
            <div className="recording-indicator">
              <div className="recording-dot"></div>
              <span>Recording: {Math.floor(recordingDuration / 60)}:{(recordingDuration % 60).toString().padStart(2, '0')}</span>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default PersonaChat;
